import type { ChannelIconProps } from '@buffer-mono/popcorn'

import { GUEST_NETWORKS } from '~publish/helpers/guestNetworks'
import type { GuestNetworkId } from '~publish/helpers/guestNetworks'

type PublicShareTarget = Readonly<
  | {
      mode: 'intent'
      intentUrl: string
    }
  | {
      mode: 'manual'
      openUrl: string
    }
>

type PublicShareDestinationConfig = PublicShareTarget &
  Readonly<{
    id: GuestNetworkId
    name?: string
    requiresImage?: boolean
  }>

export type PublicShareDestination = PublicShareDestinationConfig &
  Readonly<{
    name: string
    icon: ChannelIconProps['type']
  }>

const PUBLIC_SHARE_DESTINATION_CONFIGS: readonly PublicShareDestinationConfig[] =
  [
    { id: 'x', mode: 'intent', intentUrl: 'https://x.com/intent/tweet' },
    {
      id: 'threads',
      mode: 'intent',
      intentUrl: 'https://www.threads.com/intent/post',
    },
    {
      id: 'bluesky',
      mode: 'intent',
      intentUrl: 'https://bsky.app/intent/compose',
    },
    {
      id: 'mastodon',
      mode: 'intent',
      intentUrl: 'https://share.joinmastodon.org/',
    },
    {
      id: 'pinterest',
      requiresImage: true,
      mode: 'manual',
      openUrl: 'https://www.pinterest.com/pin-creation-tool/',
    },
    {
      id: 'facebook-page',
      name: 'Facebook',
      mode: 'manual',
      openUrl: 'https://www.facebook.com/',
    },
    {
      id: 'linkedin',
      mode: 'manual',
      openUrl: 'https://www.linkedin.com/feed/',
    },
    {
      id: 'instagram',
      mode: 'manual',
      openUrl: 'https://www.instagram.com/',
    },
    {
      id: 'tiktok',
      mode: 'manual',
      openUrl: 'https://www.tiktok.com/upload',
    },
    {
      id: 'youtube',
      mode: 'manual',
      openUrl: 'https://www.youtube.com/upload',
    },
    {
      id: 'googlebusiness',
      mode: 'manual',
      openUrl: 'https://business.google.com/',
    },
    {
      id: 'substack',
      mode: 'manual',
      openUrl: 'https://substack.com/notes',
    },
  ]

export const PUBLIC_SHARE_DESTINATIONS: readonly PublicShareDestination[] =
  PUBLIC_SHARE_DESTINATION_CONFIGS.flatMap((destination) => {
    const network = GUEST_NETWORKS.find(({ id }) => id === destination.id)
    return network
      ? [
          {
            ...destination,
            name: destination.name ?? network.name,
            icon: network.icon,
          },
        ]
      : []
  })

export function getPublicShareUrl(
  destination: PublicShareDestination,
  text: string,
): string {
  if (destination.mode === 'manual') return destination.openUrl

  if (destination.id === 'mastodon') {
    return `${destination.intentUrl}#text=${encodeURIComponent(text)}`
  }

  const url = new URL(destination.intentUrl)
  url.searchParams.set('text', text)
  return url.toString()
}

export function getPublicShareDestination(
  networkId: GuestNetworkId,
): PublicShareDestination | undefined {
  return PUBLIC_SHARE_DESTINATIONS.find(
    (destination) => destination.id === networkId,
  )
}
